import { Provider } from '@angular/core';
import { OPERATORS } from 'src/app/services/constants';
import {
  ToolbarControlDescriptor,
  TOOLBAR_CONTROLS,
} from '../../shared/contracts/toolbar-control.tokens';
import {
  TrackClusterControlComponent,
  TrackCloseControlComponent,
  TrackDimControlComponent,
  TrackEntryControlComponent,
  TrackSourceControlComponent,
  TrackTitleControlComponent,
} from './controls/toolbar-track-controls';
import { VesselHistoryControlComponent } from './controls/toolbar-vessel-controls';
import { PortCallControlComponent } from './controls/toolbar-port-call-controls';
import { NearbyControlComponent } from './controls/toolbar-nearby-controls';
import { SearchControlComponent } from './controls/toolbar-search-controls';

/**
 * VOYAGE RADAR'S toolbar controls — the ten descriptors whose components speak
 * this provider's language (D7 amendment, 2026-08-13).
 *
 * WHY A SECOND REGISTRY FILE RATHER THAN TEN MORE LINES IN THE SHARED ONE.
 * `TOOLBAR_CONTROLS` is a `multi: true` token, so registration was always
 * decentralisable — the single shared DESCRIPTORS array was a convention, not
 * a constraint. Every control here injects a Voyage Radar controller or
 * service (`VoyageTrackController`, `VoyageVesselController`,
 * `VoyagePortCallsService`, `VoyageNearbyService`, `VoyageSearchService`) or
 * reads its credit rules, which is the test that settles ownership: a control
 * that injects a provider's own state IS that provider's control, whatever the
 * folder said.
 *
 * The two suffixes are not two kinds of ownership — both halves are equally
 * Voyage Radar's. `Controller` marks the four that extend `PlaybackController`
 * and are handed to a bar as `context.controller`; `Service` marks the three in
 * `../services/` that are plain root singletons holding a subject and a fetch.
 * See the note on `VoyagePortCallsService` for why the distinction is worth a
 * suffix at all.
 *
 * WHAT THIS BUYS beyond tidiness: adding a provider's control no longer means
 * editing a shared file. That is the motivation behind **D6** — "can a
 * provider add a NOVEL card button without editing shared files?" — answered
 * here for the toolbar half. A new provider writes its own descriptors file,
 * names its controls in `SENSOR_TOOLBAR_CONFIGS`, and touches nothing in
 * `ais/shared/`.
 *
 * WHAT STAYED SHARED, and why it is not the same question: transport, labels,
 * the type filter, clusters, the live badge, the date range, the separator and
 * the playback-history entry all drive the `PlaybackController` BASE and
 * import no provider file. Several of them happen to be mounted only by Voyage
 * Radar today — `clusters` needs the `clustering` capability, which AISstream
 * does not declare — but "nobody else switched it on yet" is a fact about the
 * configs, not about the code. Imports are the test; mounts are not.
 */
const VR_DESCRIPTORS: ToolbarControlDescriptor[] = [
  // The three paid by-MMSI drawers. They stand on the LIVE bar and on the
  // standalone `vessel` bar — a question about a hull needs a hull, not a
  // moving map — and `requires` is what takes them off a provider with no
  // wallet. `slots` is the whitelist the host filters against, so this list and
  // SENSOR_TOOLBAR_CONFIGS must agree or a config entry silently does nothing.
  { id: 'port-call', component: PortCallControlComponent, slots: ['live', 'vessel'], defaultOrder: 22, requires: ['priced-actions'] },
  { id: 'nearby', component: NearbyControlComponent, slots: ['live', 'vessel'], defaultOrder: 23, requires: ['priced-actions'] },
  { id: 'search', component: SearchControlComponent, slots: ['live', 'vessel'], defaultOrder: 5, requires: ['vessel-search'] },

  // The per-vessel track bar — its own slot, stacked above the live one.
  { id: 'track-close', component: TrackCloseControlComponent, slots: ['track'], defaultOrder: -20, requires: ['track-replay'] },
  { id: 'track-title', component: TrackTitleControlComponent, slots: ['track'], defaultOrder: -10, requires: ['track-replay'] },
  // Same order as `clusters` on the other two bars, so the button does not move
  // when a track replaces them. Shows only while the fleet dim is off.
  { id: 'track-clusters', component: TrackClusterControlComponent, slots: ['track'], defaultOrder: 82, requires: ['track-replay'] },
  { id: 'track-dim', component: TrackDimControlComponent, slots: ['track'], defaultOrder: 85, requires: ['track-replay'] },
  { id: 'track-source', component: TrackSourceControlComponent, slots: ['track'], defaultOrder: 95, requires: ['track-replay'] },
  // The FOURTH standing lookup control, on the same two slots and by the same
  // rule as the three above — it asks where a hull has been, which is a question
  // about a still map, and its drawer says "select a vessel" when none is.
  //
  // `vessel` was missing here, and that is what made the empty-map bar carry two
  // of the three paid per-vessel actions and not the third. It is not only the
  // pre-refresh AOI case: a POI task NEVER draws a live layer at all (its refresh
  // returns the positionless nearby list — see `showPoiNearby`), so `dataActive`
  // stays false for its whole life and this bar is the only chrome it ever gets.
  // Tracing a hull located from the reticle's list was therefore unreachable from
  // the map on a POI task, while port calls and nearby sat right there.
  { id: 'track-entry', component: TrackEntryControlComponent, slots: ['live', 'vessel'], defaultOrder: 97, requires: ['track-replay'] },

  // Located vessels (worldwide search): ONE control — a button whose drawer
  // lists every located vessel with center/remove per row. Valid in both the
  // live slot (snapshot drawn) and the standalone `vessel` slot (empty map).
  // Renders nothing until a search has located something.
  { id: 'vessel-history', component: VesselHistoryControlComponent, slots: ['live', 'vessel'], defaultOrder: 35, requires: ['vessel-search'] },
];

/**
 * The string the host matches both this file's `operatorKey` and this
 * provider's `SensorToolbarConfig.operatorKey` against.
 *
 * DERIVED from the app-wide catalogue rather than restated (D16). The literal
 * has been written twice before: D9 needed a second copy because the config
 * lived in `shared/`, which cannot import a provider folder (R5), and D13
 * removed that one by moving the config here. Both rounds missed a THIRD copy
 * — `OPERATORS.VOYAGERADAR`, which every other screen in the app already
 * switches on (`cancelsensor`, the map component's own sensor routing). They
 * agreed by luck rather than by construction; now they cannot disagree.
 */
export const VR_OPERATOR_KEY = OPERATORS.VOYAGERADAR;

/**
 * Contributed into the same `TOOLBAR_CONTROLS` multi-token the shared registry
 * uses, so the host resolves one flat list and cannot tell which file a
 * descriptor came from. Registered alongside `TOOLBAR_CONTROL_PROVIDERS` in
 * `app.module.ts`.
 *
 * The owner is stamped HERE rather than written on each of the ten descriptors
 * above, and that is the point: ownership is a property of the FILE — every
 * control in it injects a Voyage Radar controller — so it cannot be forgotten
 * on the eleventh. A descriptor that wants to be provider-neutral does not
 * belong in this array at all; it belongs in the shared registry.
 */
export const VR_TOOLBAR_CONTROL_PROVIDERS: Provider[] = VR_DESCRIPTORS.map((d) => ({
  provide: TOOLBAR_CONTROLS,
  useValue: { ...d, operatorKey: VR_OPERATOR_KEY },
  multi: true,
}));
